import React, { useState } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import { ClipCategory } from 'services/VideoEditingService';

interface CategoryPickerProps {
  // The vocabulary to choose from (archived categories excluded by the API).
  categories: ClipCategory[];
  // Selected category SLUGS.
  value: string[];
  onChange: (slugs: string[]) => void;
  // Creates a category server-side and resolves to it. Without this the picker is
  // select-only.
  onCreate?: (name: string) => Promise<ClipCategory>;
  label?: string;
  helperText?: string;
  size?: 'small' | 'medium';
  disabled?: boolean;
}

// Distinguishes a typed-but-not-yet-created entry from a real category. Autocomplete's
// freeSolo hands back a bare string for the former and the option object for the latter.
type Option = ClipCategory | string;

/**
 * Pick clip categories, creating one on the fly when the right one doesn't exist yet.
 *
 * Categories are a MANAGED vocabulary — a real row with a stable slug — which is what makes
 * them renameable and countable, unlike the free `keywords` field next to them. So typing a
 * new one is a deliberate create (a request) rather than just another string in an array,
 * and the picker shows that: it selects the created category on success and surfaces the
 * failure inline rather than silently dropping what was typed.
 *
 * Selection is by slug so the caller sends what the API filters on; display comes from the
 * vocabulary, so a rename shows up everywhere without re-tagging a single clip.
 */
export default function CategoryPicker({
  categories,
  value,
  onChange,
  onCreate,
  label = 'Categories',
  helperText,
  size = 'small',
  disabled,
}: CategoryPickerProps) {
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const bySlug = new Map(categories.map(c => [c.slug, c]));
  // A slug with no vocabulary entry is a category that has since been ARCHIVED. Keep
  // showing it — the clip really is filed under it, and hiding it would make an unrelated
  // edit quietly drop the categorisation.
  const selected: ClipCategory[] = value.map(
    slug => bySlug.get(slug) || {
      categoryId: slug, slug, name: slug, createdBy: null, createdAt: null,
      archivedAt: new Date().toISOString(), labelCount: 0,
    },
  );

  const nameOf = (option: Option) => (typeof option === 'string' ? option : option.name);

  async function handleChange(next: Option[]) {
    setError(null);
    const slugs: string[] = [];
    const typed: string[] = [];
    for (const option of next) {
      if (typeof option === 'string') typed.push(option.trim());
      else slugs.push(option.slug);
    }

    // Nothing new typed — a plain selection change.
    if (!typed.length || !onCreate) {
      onChange(Array.from(new Set(slugs)));
      return;
    }

    setCreating(true);
    try {
      for (const name of typed) {
        if (!name) continue;
        const created = await onCreate(name);
        slugs.push(created.slug);
      }
      onChange(Array.from(new Set(slugs)));
    } catch (err: any) {
      // Keep the existing selection: dropping it because the create failed would lose
      // categories the person had already picked in the same interaction.
      setError(err?.response?.data?.error || err?.message || 'Could not create that category');
      onChange(Array.from(new Set(value.concat(slugs))));
    } finally {
      setCreating(false);
    }
  }

  return (
    <Autocomplete
      multiple
      freeSolo
      size={size}
      disabled={disabled || creating}
      options={categories}
      value={selected as Option[]}
      // Compare by slug: `selected` rebuilds fresh objects each render, so identity
      // matching would show every selected category as still available to pick.
      isOptionEqualToValue={(option, val) => nameOf(option) === nameOf(val)
        || (typeof option !== 'string' && typeof val !== 'string' && option.slug === val.slug)}
      getOptionLabel={nameOf}
      onChange={(_, next) => handleChange(next as Option[])}
      renderOption={(props, option) => (
        <li {...props} key={typeof option === 'string' ? option : option.slug}>
          {nameOf(option)}
          {typeof option !== 'string' && option.labelCount > 0 && (
            <span style={{ marginLeft: 6, color: '#94A3B8', fontSize: 12 }}>({option.labelCount})</span>
          )}
        </li>
      )}
      renderTags={(tags, getTagProps) => tags.map((option, index) => (
        <Chip
          {...getTagProps({ index })}
          key={typeof option === 'string' ? option : option.slug}
          size="small"
          label={nameOf(option)}
          // A retired category still shown on this clip reads as an outline, so it's
          // visibly different from one that's still offered.
          variant={typeof option !== 'string' && option.archivedAt ? 'outlined' : 'filled'}
        />
      ))}
      renderInput={params => (
        <TextField
          {...params}
          label={label}
          error={!!error}
          helperText={error || helperText || 'Pick one, or type a new one and press Enter to create it.'}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {creating ? <CircularProgress size={14} sx={{ mr: 1 }} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
